import {
  Badge,
  Button,
  Card,
  Divider,
  FormControl,
  IconButton,
  Icons,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useUpdateAnamnesisSymptoms, useUpdateAnamnesisSymptomsNote } from '../../../../../api/created/anamnesis.ts';
import type { GetAnamnesesByPatient200, SymptomDto } from '../../../../../api/generated/model';
import { SymptomDtoSymptomType } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { TruncateText } from '../../../../../components/TruncateText.tsx';
import { getSymptomTypeName } from '../../../../../utils/anamnesis.ts';
import { formatDateWithGermanMonthOrText } from '../../../../../utils/date.ts';
import { AddSymptomDialog } from './AddSymptomDialog.tsx';
import { EditAnamnesisItemsDialog } from './EditAnamnesisItemsDialog.tsx';

export function SymptomsCard({
  symptoms,
  symptomsNote,
  anamnesisId,
  patientId,
}: {
  symptoms: SymptomDto[];
  symptomsNote?: string | null;
  anamnesisId: string;
  patientId: string;
}) {
  const queryClient = useQueryClient();
  const [isAddSymptomDialogOpen, setIsAddSymptomDialogOpen] = useState(false);
  const [isEditSymptomsDialogOpen, setIsEditSymptomsDialogOpen] = useState(false);
  const [note, setNote] = useState(symptomsNote ?? '');
  const shouldScrollSymptoms = symptoms.length > 10;
  const isNoteDirty = note !== (symptomsNote ?? '');

  useEffect(() => {
    setNote(symptomsNote ?? '');
  }, [symptomsNote]);

  const updateSymptomsNoteMutation = useUpdateAnamnesisSymptomsNote({
    mutation: {
      onSuccess: (_, variables) =>
        queryClient.setQueryData<GetAnamnesesByPatient200>(
          getGetAnamnesesByPatientQueryKey(patientId),
          (oldAnamneses) =>
            oldAnamneses &&
            Object.fromEntries(
              Object.entries(oldAnamneses).map(([key, value]) => [
                key,
                value.id === anamnesisId ? { ...value, symptomsNote: variables.data.note } : value,
              ]),
            ),
        ),
      onError: () => toast.error('Beim Speichern der Bemerkungen ist ein Fehler aufgetreten'),
    },
  });

  const updateSymptomsMutation = useUpdateAnamnesisSymptoms({
    mutation: {
      onSuccess: (_, variables) => {
        queryClient.setQueryData<GetAnamnesesByPatient200>(
          getGetAnamnesesByPatientQueryKey(patientId),
          (oldAnamneses) =>
            oldAnamneses &&
            Object.fromEntries(
              Object.entries(oldAnamneses).map(([key, value]) => [
                key,
                value.id === anamnesisId ? { ...value, symptoms: variables.data } : value,
              ]),
            ),
        );
        void queryClient.invalidateQueries({ queryKey: getGetAnamnesesByPatientQueryKey(patientId) });
        setIsEditSymptomsDialogOpen(false);
      },
      onError: () => toast.error('Beim Bearbeiten der Symptome ist ein Fehler aufgetreten'),
    },
  });

  const handleNoteBlur = () => {
    if (note === (symptomsNote ?? '')) {
      return;
    }

    updateSymptomsNoteMutation.mutate({
      id: anamnesisId,
      data: { note },
    });
  };

  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography level="title-lg">Symptome</Typography>
          <Stack direction={'row'} gap={1}>
            <IconButton
              color={'primary'}
              disabled={!symptoms.length}
              onClick={() => setIsEditSymptomsDialogOpen(true)}
              title={'Symptome bearbeiten'}>
              <Icons.Edit />
            </IconButton>
            <Button onClick={() => setIsAddSymptomDialogOpen(true)} endDecorator={<Icons.Add />}>
              Symptom hinzufügen
            </Button>
          </Stack>
        </Stack>

        <Divider />
        {symptoms.length ? (
          <Sheet
            sx={
              shouldScrollSymptoms
                ? { maxHeight: 'calc(var(--TableCell-height, 40px) * 11)', overflow: 'auto' }
                : undefined
            }>
            <Table stickyHeader={shouldScrollSymptoms}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Seit</th>
                  <th>Symptom</th>
                </tr>
              </thead>
              <tbody>
                {symptoms.map((symptom) => (
                  <tr key={symptom.id}>
                    <td>
                      <TruncateText>{formatDateWithGermanMonthOrText(symptom.started)}</TruncateText>
                    </td>
                    <td>{getSymptomTypeName(symptom.symptomType)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level="body-sm">Noch keine Symptome vorhanden.</Typography>
          </Stack>
        )}
        <Divider />
        <Table>
          <tbody>
            <tr>
              <td style={{ paddingTop: 16, verticalAlign: 'top', width: 110 }}>
                <Badge
                  variant="solid"
                  badgeInset="50% 8px"
                  invisible={!isNoteDirty && !updateSymptomsNoteMutation.isPending}
                  color="warning"
                  sx={{ display: 'block' }}
                  slotProps={{ badge: { sx: { boxShadow: 'none' } } }}>
                  <Typography level="title-md">Bemerkungen</Typography>
                </Badge>
              </td>
              <td style={{ paddingTop: 16 }}>
                <FormControl>
                  <Textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    onBlur={handleNoteBlur}
                    minRows={3}
                    maxRows={6}
                    variant="plain"
                  />
                </FormControl>
              </td>
            </tr>
          </tbody>
        </Table>
      </Card>
      <AddSymptomDialog
        anamnesisId={anamnesisId}
        patientId={patientId}
        open={isAddSymptomDialogOpen}
        onClose={() => setIsAddSymptomDialogOpen(false)}
      />
      <EditAnamnesisItemsDialog
        open={isEditSymptomsDialogOpen}
        title={'Symptome bearbeiten'}
        typeLabel={'Symptom'}
        emptyText={'Keine Symptome vorhanden.'}
        items={symptoms.map((symptom) => ({
          id: symptom.id,
          started: symptom.started,
          type: symptom.symptomType,
        }))}
        typeOptions={Object.values(SymptomDtoSymptomType).map((type) => ({
          label: getSymptomTypeName(type),
          value: type,
        }))}
        isLoading={updateSymptomsMutation.isPending}
        onClose={() => setIsEditSymptomsDialogOpen(false)}
        onSubmit={(items) =>
          updateSymptomsMutation.mutate({
            id: anamnesisId,
            data: items.map((item) => ({
              id: item.id,
              started: item.started,
              symptomType: item.type,
            })),
          })
        }
      />
    </>
  );
}
